import { useState, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Award, Briefcase, ExternalLink, FileUser, GraduationCap, Languages, Wrench } from 'lucide-react';
import { useMe } from '@/api/auth';
import { useTesterCv } from '@/api/marketplace';
import type { CvContent, Guid } from '@/api/types';
import { Dialog } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { EmptyState, ErrorState, LoadingBlock } from '@/components/ui/States';
import { formatDate } from '@/lib/format';
import { cn } from '@/lib/cn';

/** Paths a tester without a CV may still open (the CV form itself, and the privacy notice). */
const CV_FREE = /^\/(my\/cv|privacy)(\/|$)/;

/**
 * Testers are asked for their CV once, right after signing up (or the first time they sign in without one). Nothing in it is required,
 * but it has to be saved once before the rest of the app opens; the person is then taken where they were going.
 */
export function CvGate() {
  const { data: me } = useMe();
  const location = useLocation();
  if (!me?.needsCv || CV_FREE.test(location.pathname)) return null;
  // The privacy notice comes first: it is shown over the page, so wait until it is acknowledged.
  if (me.pendingConsents?.includes('PrivacyNotice')) return null;
  const next = location.pathname + location.search + location.hash;
  return <Navigate to={`/my/cv?next=${encodeURIComponent(next)}`} replace />;
}

/** "2019 – 2022", "2021 – Halen", "2020" or nothing. */
export function yearRange(start: number | null | undefined, end: number | null | undefined, current: boolean, present: string): string {
  if (start && (end || current)) return `${start} – ${current ? present : end}`;
  if (start) return current ? `${start} – ${present}` : String(start);
  if (end) return String(end);
  return current ? present : '';
}

function Block({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-2">
      <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.06em] text-ink-3 [&>svg]:size-3.5">{icon}{title}</h3>
      {children}
    </section>
  );
}

function Chips({ items }: { items: string[] }) {
  return (
    <ul className="flex flex-wrap gap-1">
      {items.map((x) => <li key={x} className="rounded-[3px] bg-surface-3 px-1.5 py-0.5 text-xs text-ink-2">{x}</li>)}
    </ul>
  );
}

/** A CV as customers read it. Only the parts the tester filled in are shown. */
export function CvView({ cv, className }: { cv: CvContent; className?: string }) {
  const { t } = useTranslation();
  const present = t('cv.present');
  const facts = [
    cv.testingYears != null ? t('cv.yearsOfTesting', { count: cv.testingYears }) : null,
  ].filter(Boolean) as string[];
  return (
    <div className={cn('flex flex-col gap-5', className)} data-testid="cv-view">
      {(cv.summary || facts.length > 0 || cv.testingTypes.length > 0) && (
        <section className="flex flex-col gap-2">
          {facts.length > 0 && <p className="text-sm font-semibold">{facts.join(' · ')}</p>}
          {cv.summary && <p className="whitespace-pre-line text-sm text-ink-2">{cv.summary}</p>}
          {cv.testingTypes.length > 0 && <Chips items={cv.testingTypes.map((x) => t(`cv.type.${x}`))} />}
        </section>
      )}
      {cv.experience.length > 0 && (
        <Block icon={<Briefcase />} title={t('cv.experience')}>
          <ol className="flex flex-col gap-3 border-l border-line pl-3">
            {cv.experience.map((x, i) => (
              <li key={i} className="flex flex-col gap-0.5">
                <p className="text-sm"><span className="font-semibold">{x.title || t('cv.untitled')}</span>{x.company && <span className="text-ink-2"> · {x.company}</span>}</p>
                {yearRange(x.startYear, x.endYear, x.current, present) && <p className="mono text-xs text-ink-3">{yearRange(x.startYear, x.endYear, x.current, present)}</p>}
                {x.description && <p className="whitespace-pre-line text-sm text-ink-2">{x.description}</p>}
              </li>
            ))}
          </ol>
        </Block>
      )}
      {cv.education.length > 0 && (
        <Block icon={<GraduationCap />} title={t('cv.education')}>
          <ul className="flex flex-col gap-2">
            {cv.education.map((x, i) => (
              <li key={i} className="text-sm">
                <p className="font-semibold">{x.school || t('cv.untitled')}</p>
                <p className="text-ink-2">{[x.degree, x.field].filter(Boolean).join(' · ')}{yearRange(x.startYear, x.endYear, false, present) && <span className="mono ml-2 text-xs text-ink-3">{yearRange(x.startYear, x.endYear, false, present)}</span>}</p>
              </li>
            ))}
          </ul>
        </Block>
      )}
      {cv.certificates.length > 0 && (
        <Block icon={<Award />} title={t('cv.certificates')}>
          <ul className="flex flex-col gap-1 text-sm">
            {cv.certificates.map((x, i) => (
              <li key={i}><span className="font-medium">{x.name || t('cv.untitled')}</span>{x.issuer && <span className="text-ink-2"> · {x.issuer}</span>}{x.year && <span className="mono ml-2 text-xs text-ink-3">{x.year}</span>}</li>
            ))}
          </ul>
        </Block>
      )}
      {(cv.languages.length > 0 || cv.tools.length > 0) && (
        <div className="grid gap-5 sm:grid-cols-2">
          {cv.languages.length > 0 && (
            <Block icon={<Languages />} title={t('cv.languages')}>
              <ul className="flex flex-col gap-0.5 text-sm">
                {cv.languages.map((x) => <li key={x.name}>{x.name}{x.level && <span className="text-ink-3"> · {t(`cv.level.${x.level}`)}</span>}</li>)}
              </ul>
            </Block>
          )}
          {cv.tools.length > 0 && <Block icon={<Wrench />} title={t('cv.tools')}><Chips items={cv.tools} /></Block>}
        </div>
      )}
      {cv.links.length > 0 && (
        <Block icon={<ExternalLink />} title={t('cv.links')}>
          <ul className="flex flex-col gap-1 text-sm">
            {cv.links.map((x, i) => x.url && (
              <li key={i}>
                <a href={x.url} target="_blank" rel="noopener noreferrer nofollow" className="text-accent-text hover:underline">{x.label || x.url.replace(/^https?:\/\//, '')}</a>
              </li>
            ))}
          </ul>
        </Block>
      )}
    </div>
  );
}

export function isCvEmpty(cv: CvContent | null | undefined): boolean {
  return !cv || (!cv.summary && cv.testingYears == null && cv.experience.length === 0 && cv.education.length === 0 && cv.certificates.length === 0 &&
    cv.languages.length === 0 && cv.tools.length === 0 && cv.testingTypes.length === 0 && cv.links.length === 0);
}

/** A tester's CV, loaded for a customer (or a team lead). */
export function TesterCv({ userId, className }: { userId: Guid; className?: string }) {
  const { t, i18n } = useTranslation();
  const query = useTesterCv(userId);
  if (query.isPending) return <LoadingBlock rows={4} />;
  if (query.isError) return <ErrorState error={query.error} />;
  const { content, updatedAt } = query.data;
  if (isCvEmpty(content)) return <EmptyState icon={<FileUser />} title={t('cv.emptyTitle')}>{t('cv.emptyBody', { name: query.data.displayName })}</EmptyState>;
  return (
    <div className={className}>
      <CvView cv={content!} />
      {updatedAt && <p className="mt-4 text-xs text-ink-3">{t('cv.updatedAt', { at: formatDate(updatedAt, i18n.language) })}</p>}
    </div>
  );
}

/** "CV" button that opens the tester's CV in a dialog (applicant lists, team members). */
export function CvButton({ userId, name, className }: { userId: Guid; name: string; className?: string }) {
  const { t } = useTranslation();
  return (
    <CvDialogTrigger userId={userId} name={name}>
      {(open) => <Button size="sm" variant="ghost" icon={<FileUser className="size-3.5" />} className={className} onClick={open}>{t('cv.open')}</Button>}
    </CvDialogTrigger>
  );
}


function CvDialogTrigger({ userId, name, children }: { userId: Guid; name: string; children: (open: () => void) => ReactNode }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <>
      {children(() => setOpen(true))}
      <Dialog open={open} onOpenChange={setOpen} size="lg" title={t('cv.titleOf', { name })} footer={<Button onClick={() => setOpen(false)}>{t('common.close')}</Button>}>
        {open && <TesterCv userId={userId} />}
      </Dialog>
    </>
  );
}
